import React, {useState, useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import {useSidebarMinimized} from './useSidebarMinimized';

interface SidebarTooltipProps {
    title: string;
    children: React.ReactElement;
}

const SidebarTooltip = ({title, children}: SidebarTooltipProps): React.ReactElement => {
    const isMinimized = useSidebarMinimized();
    const [pos, setPos] = useState<{top: number; left: number} | null>(null);
    const allowRef = useRef(false);

    useEffect(() => {
        allowRef.current = false;
        setPos(null);
        if (isMinimized) {
            const timer = setTimeout(() => {
                allowRef.current = true;
            }, 400);
            return () => clearTimeout(timer);
        }
    }, [isMinimized]);

    if (!isMinimized) return children;

    const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
        if (!allowRef.current) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setPos({top: rect.top + rect.height / 2, left: rect.right + 10});
    };

    const handleMouseLeave = () => setPos(null);

    const child = React.cloneElement(children, {onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave});

    return (
        <>
            {child}
            {pos &&
                createPortal(
                    <div
                        style={{
                            position: 'fixed',
                            top: pos.top,
                            left: pos.left,
                            transform: 'translateY(-50%)',
                            background: '#1e1e2d',
                            color: '#fff',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            zIndex: 1055,
                        }}
                    >
                        {title}
                    </div>,
                    document.body
                )}
        </>
    );
};

export {SidebarTooltip};
